// ===========================================
// Student Progress Management Page
// ===========================================
// View and manage student gamification levels and progress

import { useState } from 'react';
import {
  Box,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Whatshot as FireIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAllProgress, useAdvanceLevel, useGamificationLevels } from '../../hooks/useGamification';
import { StudentProgress } from '../../types/gamification.types';

// ===========================================
// COMPONENT
// ===========================================

export default function StudentProgressPage() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentProgress | null>(null);

  // Fetch gamification levels for the filter dropdown
  const { data: levels = [] } = useGamificationLevels();

  // Fetch progress data
  const { data, isLoading, error } = useAllProgress({
    page: page + 1,
    limit: rowsPerPage,
    level: levelFilter || undefined,
  });

  const advanceLevelMutation = useAdvanceLevel();

  // Handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAdvanceLevel = (student: StudentProgress) => {
    setSelectedStudent(student);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAdvance = async () => {
    if (!selectedStudent) return;

    try {
      await advanceLevelMutation.mutateAsync(selectedStudent.studentId);
      setConfirmDialogOpen(false);
      setSelectedStudent(null);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleCancelAdvance = () => {
    setConfirmDialogOpen(false);
    setSelectedStudent(null);
  };

  // Check if student is at max level
  const isAtMaxLevel = (currentLevelId: string): boolean => {
    if (levels.length === 0) return false;
    return currentLevelId === levels[levels.length - 1].id;
  };

  // Get next level name
  const getNextLevelName = (currentLevelId: string): string | null => {
    const currentIndex = levels.findIndex((l) => l.id === currentLevelId);
    if (currentIndex === -1 || currentIndex >= levels.length - 1) {
      return null;
    }
    const nextLevel = levels[currentIndex + 1];
    return nextLevel.name;
  };

  const progressData = data?.data ?? [];
  const totalCount = data?.pagination?.total ?? 0;

  return (
    <Box>
      <PageHeader
        title="Student Progress"
        subtitle="View and manage student gamification levels and progress"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Student Progress' },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load student progress. Please try again.
        </Alert>
      )}

      {/* Filter Bar */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel>Filter by Level</InputLabel>
          <Select
            value={levelFilter}
            onChange={(e) => {
              setLevelFilter(e.target.value);
              setPage(0);
            }}
            label="Filter by Level"
          >
            <MenuItem value="">
              <em>All Levels</em>
            </MenuItem>
            {levels.map((level) => (
              <MenuItem key={level.id} value={level.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: level.color || '#4580E4',
                    }}
                  />
                  {level.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body2" color="text.secondary">
          {totalCount} {totalCount === 1 ? 'student' : 'students'}
        </Typography>
      </Box>

      {/* Progress Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Current Level</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Lessons Attended</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Current Streak</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pieces Completed</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Skills Completed</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : progressData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No student progress data found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              progressData.map((row) => {
                // PascalCase, as `progressInclude` sends it (#201). This read was
                // `row.currentLevel` — never sent — and a `return null` guard on it
                // dropped EVERY row. `ProgramLevel` is a required relation, so the "–"
                // below is only a safety net: a student is never hidden again.
                const level = row.ProgramLevel;
                const levelColor = level?.color || '#4580E4';
                const levelName = level?.name ?? '–';
                const mascotName = level?.mascot || '';
                const completed = row.totalPiecesCompleted;
                const assigned = row.totalPiecesAssigned;
                const percentage = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.Student
                        ? `${row.Student.firstName} ${row.Student.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={levelName}
                          size="small"
                          sx={{
                            bgcolor: levelColor,
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                        {level?.mascotEmoji && level.mascotEmoji.startsWith('/') ? (
                          <Box
                            component="img"
                            src={level.mascotEmoji}
                            alt={mascotName}
                            sx={{ width: 24, height: 24, objectFit: 'contain' }}
                          />
                        ) : mascotName ? (
                          <Typography variant="caption" color="text.secondary">
                            ({mascotName})
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {row.totalLessonsAttended}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                        {row.currentStreak > 0 && (
                          <FireIcon fontSize="small" sx={{ color: '#ff6f00' }} />
                        )}
                        <Typography variant="body2" fontWeight={600}>
                          {row.currentStreak} {row.currentStreak === 1 ? 'week' : 'weeks'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {completed} / {assigned}
                      </Typography>
                      {assigned > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({percentage}%)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {row.totalSkillsCompleted}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          isAtMaxLevel(row.currentLevelId)
                            ? 'Student is at maximum level'
                            : `Advance to ${getNextLevelName(row.currentLevelId)}`
                        }
                      >
                        <span>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleAdvanceLevel(row)}
                            disabled={isAtMaxLevel(row.currentLevelId)}
                          >
                            <TrendingUpIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalCount > 0 && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}

      {/* Advance Level Confirmation */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Advance Student Level"
        message={
          selectedStudent
            ? `Are you sure you want to advance ${selectedStudent.Student?.firstName} ${selectedStudent.Student?.lastName} from ${selectedStudent.ProgramLevel?.name ?? 'their current level'} to ${getNextLevelName(selectedStudent.currentLevelId)}?`
            : ''
        }
        confirmLabel="Advance Level"
        confirmColor="primary"
        loading={advanceLevelMutation.isPending}
        onConfirm={handleConfirmAdvance}
        onCancel={handleCancelAdvance}
      />
    </Box>
  );
}
